/**
 * Administration → approve a sensitive Veeva user change (ADR-0038).
 *
 * Reached from the approval email (`/admin/approvals/veeva/:changeId`) and
 * from the ticket's detail page. Shows who asked, the Veeva user, current vs
 * requested values, the ticket, status and expiry; **Approve** applies it in
 * Veeva, **Reject** changes nothing — both need a reason, and the requester
 * can never decide their own request (the server refuses and audits it).
 */

import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Anchor,
  Button,
  Group,
  Loader,
  Paper,
  Stack,
  Table,
  Text,
  Textarea,
  Title,
} from "@mantine/core";
import { IconCheck, IconX } from "@tabler/icons-react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { useAccessToken } from "@/features/auth/useAccessToken";
import {
  ChangeTable,
  ChangeStatusBadge,
} from "@/features/solutions/validation/second-line-support/ProposalCard";
import { decideApproval, getApproval, type ChangeOut } from "@/shared/api/userAdmin";
import { toastApiError, toastSuccess } from "@/shared/ui/toast";

function fmt(ts: string | null | undefined): string {
  return ts ? new Date(ts).toLocaleString() : "—";
}

export function VeevaApproval() {
  const { changeId = "" } = useParams();
  const getToken = useAccessToken();
  const [change, setChange] = useState<ChangeOut | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState<"approve" | "reject" | null>(null);

  const load = useCallback(async () => {
    try {
      setChange(await getApproval(await getToken(), changeId));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load the request.");
    }
  }, [changeId, getToken]);

  useEffect(() => {
    void load();
  }, [load]);

  const decide = async (decision: "approve" | "reject") => {
    setBusy(decision);
    try {
      const out = await decideApproval(await getToken(), changeId, {
        decision,
        reason: reason.trim(),
      });
      setChange(out);
      setReason("");
      if (out.status === "applied") toastSuccess("Approved", "The change was applied in Veeva.");
      else if (out.status === "rejected") toastSuccess("Rejected", "Nothing was changed in Veeva.");
    } catch (err) {
      toastApiError("Could not record the decision", err);
      void load();
    } finally {
      setBusy(null);
    }
  };

  if (error) {
    return (
      <Alert color="red" variant="light" title="Request not available">
        {error}
      </Alert>
    );
  }
  if (!change) return <Loader size="sm" />;

  const expired = new Date(change.expires_at_utc).getTime() < Date.now();
  const rows: [string, string][] = [
    ["Ticket", change.ticket_number ?? "—"],
    [
      "Requested by",
      `${change.requested_by_display_name ?? change.requested_by_user_id}${
        change.requested_by_email ? ` <${change.requested_by_email}>` : ""
      }`,
    ],
    ["Veeva connection", change.connection_name ?? change.connection_id],
    ["Veeva user", change.kind === "create" ? `new — ${change.target_label}` : change.target_label],
    ["Requested", fmt(change.created_at_utc)],
    ["Expires", fmt(change.expires_at_utc)],
    ["Needs approval because of", change.sensitive_fields.join(", ")],
  ];
  if (change.decided_at_utc) {
    rows.push([
      "Decided",
      `${fmt(change.decided_at_utc)} by ${change.decided_by_display_name ?? "—"}`,
    ]);
  }

  return (
    <Stack gap="md" maw={900}>
      <Group justify="space-between" wrap="wrap">
        <Title order={2}>
          {change.kind === "create" ? "Approve a new Veeva user" : "Approve a Veeva user change"}
        </Title>
        <ChangeStatusBadge status={change.status} />
      </Group>
      <Paper withBorder p="md" radius="md">
        <Table fz="sm">
          <Table.Tbody>
            {rows.map(([k, v]) => (
              <Table.Tr key={k}>
                <Table.Td w={220} c="dimmed">
                  {k}
                </Table.Td>
                <Table.Td>{v}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Paper>
      <ChangeTable change={change} />
      {change.result_message && (
        <Alert color={change.status === "applied" ? "green" : "red"} variant="light">
          {change.result_message}
        </Alert>
      )}
      {change.decision_reason && (
        <Text size="sm">
          <strong>Reason:</strong> {change.decision_reason}
        </Text>
      )}
      {change.can_decide && !expired ? (
        <Paper withBorder p="md" radius="md">
          <Stack gap="sm">
            <Textarea
              label="Reason"
              description="Required. Recorded with your decision and emailed to the requester."
              value={reason}
              onChange={(e) => setReason(e.currentTarget.value)}
              minRows={2}
              autosize
              required
            />
            <Group>
              <Button
                color="green"
                leftSection={<IconCheck size={16} />}
                loading={busy === "approve"}
                disabled={reason.trim().length < 3 || busy !== null}
                onClick={() => void decide("approve")}
                data-testid="approve"
              >
                Approve and apply in Veeva
              </Button>
              <Button
                color="red"
                variant="light"
                leftSection={<IconX size={16} />}
                loading={busy === "reject"}
                disabled={reason.trim().length < 3 || busy !== null}
                onClick={() => void decide("reject")}
                data-testid="reject"
              >
                Reject
              </Button>
            </Group>
          </Stack>
        </Paper>
      ) : (
        change.status === "awaiting_approval" && (
          <Alert color="gray" variant="light">
            {expired
              ? "This request has expired and can no longer be approved."
              : "Only an administrator can decide this request."}
          </Alert>
        )
      )}
      {change.ticket_id && (
        <Anchor component={RouterLink} to={`/admin/tickets/${change.ticket_id}`} size="sm">
          Open ticket {change.ticket_number}
        </Anchor>
      )}
    </Stack>
  );
}
